import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Chip from '@material-ui/core/Chip';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import InputAdornment from '@material-ui/core/InputAdornment';
import MenuItem from '@material-ui/core/MenuItem';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import SyncIcon from '@material-ui/icons/Sync';
import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';
import {
  EmptyState,
  InfoCard,
  Link,
  Progress,
  StatusOK,
  StatusPending,
  StatusWarning,
} from '@backstage/core-components';
import {
  alertApiRef,
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { useEntity } from '@backstage/plugin-catalog-react';
import { Entity } from '@backstage/catalog-model';

// Shapes returned by the backend's /api/openbao/.../config
// (packages/backend/src/plugins/openbao).
export type ConfigFileInfo = {
  index: number;
  example: string;
  mode: 'env' | 'file';
  key?: string;
  mount?: Mount;
  mountPath?: string;
  exampleUrl?: string;
};

type ConfigListing = {
  basePath?: string;
  environments: string[];
  files: ConfigFileInfo[];
};

type Mount = 'config' | 'secret';

export type EnvDetail = {
  mode: 'env';
  environment: string;
  paths: Record<Mount, string>;
  exampleError?: string;
  example?: { key: string; value: string; secret: boolean }[];
  current: Record<Mount, Record<string, string>>;
  versions: Record<Mount, number>;
};

type FileDetail = {
  mode: 'file';
  environment: string;
  path: string;
  key: string;
  exampleError?: string;
  exampleText?: string;
  exists: boolean;
  content?: string;
  version: number;
};

export const CONFIG_FILES_ANNOTATION = 'kjbnlabs.in/config-files';

export const hasConfigFiles = (entity: Entity) =>
  Boolean(entity.metadata.annotations?.[CONFIG_FILES_ANNOTATION]?.trim());

// One row of the env table: every key of the example, then keys that are
// only in OpenBao. `mount` is where the key is today (undefined = missing).
export type EnvRow = {
  key: string;
  example?: string;
  exampleSecret: boolean;
  inExample: boolean;
  mount?: Mount;
  value?: string;
};

export function buildEnvRows(detail: EnvDetail): EnvRow[] {
  const where = (key: string): Mount | undefined => {
    if (key in detail.current.config) return 'config';
    if (key in detail.current.secret) return 'secret';
    return undefined;
  };
  const rows: EnvRow[] = (detail.example ?? []).map(e => {
    const mount = where(e.key);
    return {
      key: e.key,
      example: e.value,
      exampleSecret: e.secret,
      inExample: true,
      mount,
      value: mount ? detail.current[mount][e.key] : undefined,
    };
  });
  const seen = new Set(rows.map(r => r.key));
  for (const mount of ['config', 'secret'] as const) {
    for (const key of Object.keys(detail.current[mount]).sort()) {
      if (seen.has(key)) continue;
      seen.add(key);
      rows.push({
        key,
        exampleSecret: false,
        inExample: false,
        mount,
        value: detail.current[mount][key],
      });
    }
  }
  return rows;
}

// Lines that differ between two texts (same position, or added/removed at the
// end); enough for "you are changing N lines" before saving.
export function changedLineCount(before: string, after: string) {
  const a = before.split('\n');
  const b = after.split('\n');
  let n = 0;
  for (let i = 0; i < Math.max(a.length, b.length); i++) {
    if (a[i] !== b[i]) n++;
  }
  return n;
}

const mono = { fontFamily: 'monospace', fontSize: 13 };

const useEntityPath = () => {
  const { entity } = useEntity();
  return `entities/${encodeURIComponent(
    entity.metadata.namespace ?? 'default',
  )}/${encodeURIComponent(entity.kind)}/${encodeURIComponent(
    entity.metadata.name,
  )}`;
};

const useOpenbaoFetch = () => {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const entityPath = useEntityPath();
  return useCallback(
    async (path: string, init?: RequestInit) => {
      const baseUrl = await discoveryApi.getBaseUrl('openbao');
      const res = await fetchApi.fetch(`${baseUrl}/${entityPath}${path}`, {
        ...init,
        headers: init?.body ? { 'Content-Type': 'application/json' } : {},
      });
      if (!res.ok) {
        const body = await res.json().catch(() => undefined);
        throw new Error(body?.error?.message ?? `HTTP ${res.status}`);
      }
      return res.json();
    },
    [discoveryApi, fetchApi, entityPath],
  );
};

const ConfirmDialog = ({
  open,
  title,
  children,
  onCancel,
  onConfirm,
  environment,
}: {
  open: boolean;
  title: string;
  children: ReactNode;
  onCancel: () => void;
  onConfirm: () => void;
  environment: string;
}) => (
  <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
    <DialogTitle>{title}</DialogTitle>
    <DialogContent>
      {children}
      {environment !== 'dev' && (
        <DialogContentText color="error">
          This is {environment.toUpperCase()}, not dev.
        </DialogContentText>
      )}
    </DialogContent>
    <DialogActions>
      <Button onClick={onCancel}>Cancel</Button>
      <Button color="primary" variant="contained" onClick={onConfirm}>
        Save
      </Button>
    </DialogActions>
  </Dialog>
);

const useSaver = (onSave: (body: object) => Promise<void>) => {
  const alertApi = useApi(alertApiRef);
  const [saving, setSaving] = useState(false);
  const run = async (body: object, what: string) => {
    setSaving(true);
    try {
      await onSave(body);
      alertApi.post({
        message: `Saved ${what}`,
        severity: 'success',
        display: 'transient',
      });
      return true;
    } catch (e) {
      alertApi.post({
        message: `Not saved: ${(e as Error).message}`,
        severity: 'error',
      });
      return false;
    } finally {
      setSaving(false);
    }
  };
  return { saving, run };
};

const FileEditor = ({
  detail,
  onSave,
}: {
  detail: FileDetail;
  onSave: (body: object) => Promise<void>;
}) => {
  const original = detail.content ?? '';
  const [text, setText] = useState(original);
  const [confirm, setConfirm] = useState(false);
  const { saving, run } = useSaver(onSave);

  useEffect(() => setText(detail.content ?? ''), [detail]);

  const changed = text !== original;
  const env = detail.environment.toUpperCase();

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2">Example (repo)</Typography>
          <TextField
            multiline
            fullWidth
            variant="outlined"
            minRows={18}
            maxRows={40}
            value={detail.exampleText ?? ''}
            InputProps={{ readOnly: true, style: mono }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2">
            {env}: {detail.path} → {detail.key}{' '}
            {detail.exists ? `(v${detail.version})` : '(not in OpenBao yet)'}
          </Typography>
          <TextField
            multiline
            fullWidth
            variant="outlined"
            minRows={18}
            maxRows={40}
            value={text}
            onChange={e => setText(e.target.value)}
            InputProps={{ style: mono }}
          />
        </Grid>
      </Grid>
      <Box mt={2} display="flex" style={{ gap: 8 }}>
        <Button
          variant="contained"
          color="primary"
          disabled={!changed || saving}
          onClick={() => setConfirm(true)}
        >
          Save
        </Button>
        <Button disabled={!changed || saving} onClick={() => setText(original)}>
          Reset
        </Button>
        {detail.exampleText !== undefined && (
          <Button
            disabled={saving}
            onClick={() => setText(detail.exampleText ?? '')}
          >
            Copy example into editor
          </Button>
        )}
      </Box>
      <ConfirmDialog
        open={confirm}
        environment={detail.environment}
        title={`Save ${detail.key} in ${env}?`}
        onCancel={() => setConfirm(false)}
        onConfirm={async () => {
          setConfirm(false);
          await run({ content: text, version: detail.version }, detail.key);
        }}
      >
        <DialogContentText>
          {changedLineCount(original, text)} line(s) changed. This writes a new
          version of {detail.path}; other keys in it stay as they are.
        </DialogContentText>
      </ConfirmDialog>
    </>
  );
};

const EnvEditor = ({
  detail,
  onSave,
}: {
  detail: EnvDetail;
  onSave: (body: object) => Promise<void>;
}) => {
  const rows = useMemo(() => buildEnvRows(detail), [detail]);
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [newMounts, setNewMounts] = useState<Record<string, Mount>>({});
  const [revealed, setRevealed] = useState<Record<string, boolean>>({});
  const [confirm, setConfirm] = useState(false);
  const { saving, run } = useSaver(onSave);

  useEffect(() => {
    setEdits({});
    setNewMounts({});
    setRevealed({});
  }, [detail]);

  const mountOf = (r: EnvRow): Mount => r.mount ?? newMounts[r.key] ?? 'secret';
  const changes = rows
    .filter(r => r.key in edits && edits[r.key] !== r.value)
    .map(r => ({ key: r.key, value: edits[r.key], mount: mountOf(r) }));
  const missing = rows.filter(r => r.inExample && !r.mount).length;
  const env = detail.environment.toUpperCase();

  return (
    <>
      <Typography variant="body2" color="textSecondary" paragraph>
        {env}: {detail.paths.config} (v{detail.versions.config}) and{' '}
        {detail.paths.secret} (v{detail.versions.secret})
        {missing > 0 &&
          ` · ${missing} key(s) in the example are not in OpenBao`}
      </Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Key</TableCell>
            <TableCell>Example</TableCell>
            <TableCell style={{ width: '40%' }}>Value</TableCell>
            <TableCell>Stored in</TableCell>
            <TableCell>Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map(r => {
            const mount = mountOf(r);
            const hidden = mount === 'secret' && !revealed[r.key];
            const value = r.key in edits ? edits[r.key] : r.value ?? '';
            const isChanged = r.key in edits && edits[r.key] !== r.value;
            return (
              <TableRow key={r.key}>
                <TableCell style={mono}>{r.key}</TableCell>
                <TableCell style={mono}>
                  {r.exampleSecret ? '(secret)' : r.example ?? ''}
                </TableCell>
                <TableCell>
                  <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    type={hidden ? 'password' : 'text'}
                    placeholder={r.mount ? '' : 'not set'}
                    value={value}
                    onChange={e =>
                      setEdits({ ...edits, [r.key]: e.target.value })
                    }
                    InputProps={{
                      style: mono,
                      endAdornment:
                        mount === 'secret' ? (
                          <InputAdornment position="end">
                            <IconButton
                              size="small"
                              aria-label={hidden ? 'Show value' : 'Hide value'}
                              onClick={() =>
                                setRevealed({
                                  ...revealed,
                                  [r.key]: !revealed[r.key],
                                })
                              }
                            >
                              {hidden ? <Visibility /> : <VisibilityOff />}
                            </IconButton>
                          </InputAdornment>
                        ) : undefined,
                    }}
                  />
                </TableCell>
                <TableCell>
                  {r.mount ? (
                    <Chip size="small" label={r.mount} />
                  ) : (
                    <TextField
                      select
                      size="small"
                      value={mount}
                      onChange={e =>
                        setNewMounts({
                          ...newMounts,
                          [r.key]: e.target.value as Mount,
                        })
                      }
                    >
                      <MenuItem value="secret">secret</MenuItem>
                      <MenuItem value="config">config</MenuItem>
                    </TextField>
                  )}
                </TableCell>
                <TableCell>
                  {isChanged && <StatusPending>Changed</StatusPending>}
                  {!isChanged && !r.mount && (
                    <StatusWarning>Not in OpenBao</StatusWarning>
                  )}
                  {!isChanged && r.mount && !r.inExample && (
                    <StatusPending>Not in example</StatusPending>
                  )}
                  {!isChanged && r.mount && r.inExample && (
                    <StatusOK>OK</StatusOK>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      <Box mt={2} display="flex" style={{ gap: 8 }}>
        <Button
          variant="contained"
          color="primary"
          disabled={!changes.length || saving}
          onClick={() => setConfirm(true)}
        >
          Save {changes.length ? `(${changes.length})` : ''}
        </Button>
        <Button
          disabled={!changes.length || saving}
          onClick={() => setEdits({})}
        >
          Reset
        </Button>
      </Box>
      <ConfirmDialog
        open={confirm}
        environment={detail.environment}
        title={`Save ${changes.length} key(s) in ${env}?`}
        onCancel={() => setConfirm(false)}
        onConfirm={async () => {
          setConfirm(false);
          await run(
            { changes, versions: detail.versions },
            `${changes.length} key(s)`,
          );
        }}
      >
        <DialogContentText component="div">
          <ul>
            {changes.map(c => (
              <li key={c.key}>
                <code>{c.key}</code> → {c.mount}
              </li>
            ))}
          </ul>
          New keys are added; nothing is removed. Each changed path gets a new
          version.
        </DialogContentText>
      </ConfirmDialog>
    </>
  );
};

// After a save: the pods still run with the old values until the
// ExternalSecret refreshes (up to 1 h). Sync now runs the app's ArgoCD sync,
// whose PreSync hook forces the refresh; Reloader then restarts the pods.
const SyncPrompt = ({ environment }: { environment: string }) => {
  const { entity } = useEntity();
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const alertApi = useApi(alertApiRef);
  const entityPath = useEntityPath();
  const [busy, setBusy] = useState(false);

  if (!entity.metadata.annotations?.['argocd/app-name']) {
    return (
      <Box mt={2}>
        <Typography variant="body2" color="textSecondary">
          Saved. The pods pick it up when the ExternalSecret next refreshes (up
          to 1 h), or after a sync in ArgoCD.
        </Typography>
      </Box>
    );
  }

  const sync = async () => {
    setBusy(true);
    try {
      const baseUrl = await discoveryApi.getBaseUrl('argocd-sync');
      const res = await fetchApi.fetch(
        `${baseUrl}/${entityPath}/sync?instance=${encodeURIComponent(
          environment,
        )}`,
        { method: 'POST' },
      );
      if (!res.ok) {
        const body = await res.json().catch(() => undefined);
        throw new Error(body?.error?.message ?? `HTTP ${res.status}`);
      }
      alertApi.post({
        message: `Sync started in ${environment.toUpperCase()}; pods restart once the secret refreshes`,
        severity: 'info',
        display: 'transient',
      });
    } catch (e) {
      alertApi.post({
        message: `Sync not started: ${(e as Error).message}`,
        severity: 'error',
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box mt={2} display="flex" alignItems="center" style={{ gap: 16 }}>
      <Button
        variant="outlined"
        color="primary"
        startIcon={<SyncIcon />}
        disabled={busy}
        onClick={sync}
      >
        Sync now ({environment.toUpperCase()})
      </Button>
      <Typography variant="body2" color="textSecondary">
        Saved in OpenBao. Pods get it after a sync, or when the ExternalSecret
        next refreshes (up to 1 h).
      </Typography>
    </Box>
  );
};

const ConfigFileCard = ({
  file,
  environment,
}: {
  file: ConfigFileInfo;
  environment: string;
}) => {
  const call = useOpenbaoFetch();
  const [detail, setDetail] = useState<EnvDetail | FileDetail>();
  const [error, setError] = useState<string>();
  const [saved, setSaved] = useState(false);

  const load = useCallback(async () => {
    setError(undefined);
    try {
      setDetail(
        await call(
          `/config/files/${file.index}?environment=${encodeURIComponent(
            environment,
          )}`,
        ),
      );
    } catch (e) {
      setError((e as Error).message);
    }
  }, [call, file.index, environment]);

  useEffect(() => {
    load();
  }, [load]);

  const save = async (body: object) => {
    await call(
      `/config/files/${file.index}?environment=${encodeURIComponent(
        environment,
      )}`,
      { method: 'PUT', body: JSON.stringify(body) },
    );
    setSaved(true);
    await load();
  };

  const subheader =
    file.mode === 'env'
      ? 'Environment variables (envFrom)'
      : `Mounted file ${file.key}${
          file.mountPath ? ` at ${file.mountPath}` : ''
        }`;

  return (
    <InfoCard
      title={
        <>
          {file.example}{' '}
          {file.exampleUrl && (
            <Typography variant="caption">
              <Link to={file.exampleUrl}>view in repo</Link>
            </Typography>
          )}
        </>
      }
      subheader={subheader}
    >
      {error && (
        <Box mb={2}>
          <StatusWarning>{error}</StatusWarning>{' '}
          <Button size="small" onClick={load}>
            Retry
          </Button>
        </Box>
      )}
      {!detail && !error && <Progress />}
      {detail?.exampleError && (
        <Box mb={2}>
          <StatusWarning>{detail.exampleError}</StatusWarning>
        </Box>
      )}
      {detail?.mode === 'file' && <FileEditor detail={detail} onSave={save} />}
      {detail?.mode === 'env' && <EnvEditor detail={detail} onSave={save} />}
      {saved && <SyncPrompt environment={environment} />}
    </InfoCard>
  );
};

// Config tab: the service's declared config files, the example from its repo
// next to the value in OpenBao, for one environment at a time. Saving creates
// a new KV version; Sync now lets ArgoCD's PreSync hook refresh the
// ExternalSecret so the pods restart with it.
export const ConfigEditorContent = () => {
  const call = useOpenbaoFetch();
  const [listing, setListing] = useState<ConfigListing>();
  const [error, setError] = useState<string>();
  const [environment, setEnvironment] = useState<string>();

  useEffect(() => {
    let cancelled = false;
    call('/config')
      .then((body: ConfigListing) => {
        if (cancelled) return;
        setListing(body);
        setEnvironment(body.environments[0]);
      })
      .catch(e => !cancelled && setError((e as Error).message));
    return () => {
      cancelled = true;
    };
  }, [call]);

  if (error) {
    return (
      <EmptyState
        missing="info"
        title="Config can't be loaded"
        description={error}
      />
    );
  }
  if (!listing) return <Progress />;
  if (!listing.files.length) {
    return (
      <EmptyState
        missing="field"
        title="No config files declared"
        description={`Add ${CONFIG_FILES_ANNOTATION} to this component's catalog-info.yaml to edit its config here.`}
      />
    );
  }
  if (!environment) {
    return (
      <EmptyState
        missing="info"
        title="No environment you may edit"
        description="Viewing and changing config needs openbao.config.<environment>. Ask DevOps."
      />
    );
  }

  return (
    <Grid container spacing={3} direction="column">
      <Grid item>
        <TextField
          select
          label="Environment"
          variant="outlined"
          size="small"
          value={environment}
          onChange={e => setEnvironment(e.target.value)}
          style={{ minWidth: 200 }}
        >
          {listing.environments.map(name => (
            <MenuItem key={name} value={name}>
              {name.toUpperCase()}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      {listing.files.map(file => (
        <Grid item key={`${environment}-${file.index}`}>
          <ConfigFileCard file={file} environment={environment} />
        </Grid>
      ))}
    </Grid>
  );
};
